/**
 * Iter60.p41 — Active Shift Rule info banner + Worked-cell tooltip helpers.
 *
 * The banner sits just under the Daily Attendance page title and tells
 * anyone reviewing attendance how "Worked" is being calculated for the
 * currently-visible module. The copy MUST unambiguously state that a
 * paid break is INCLUDED in working hours (the user's requirement after
 * confirming Break-Is-Paid as the platform-wide rule).
 *
 * The tooltip is the row-level counterpart: hovering the Worked cell
 * surfaces "Elapsed X · Break Ym (paid) → Worked X" so an operator
 * doesn't have to open Edit-attendance to see the math.
 *
 * Backend: uses the existing `GET /api/shift-rules?module_id=…&status=active`
 * endpoint. No new route.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";

import { api } from "../lib/api";


/** Minutes → "Xh YYm" (e.g. 465 → "7h 45m"). */
export function fmtHM(min) {
    const m = Math.max(0, Math.round(Number(min) || 0));
    const h = Math.floor(m / 60);
    const r = m % 60;
    return `${h}h ${String(r).padStart(2, "0")}m`;
}


/**
 * Build the "Elapsed X · Break Ym (paid) → Worked X" tooltip string for
 * an attendance row. Falls back gracefully when clocks or shift-rule
 * fields are missing.
 *
 * @param {object} row attendance_daily row
 * @param {object|null} rule active shift rule for the row's module (optional)
 * @returns {string|null} tooltip title, or null if the row is missing clocks
 */
export function buildWorkedTooltip(row, rule) {
    if (!row?.first_clock_in || !row?.last_clock_out) return null;
    const inMs = Date.parse(row.first_clock_in);
    const outMs = Date.parse(row.last_clock_out);
    if (!isFinite(inMs) || !isFinite(outMs) || outMs <= inMs) return null;
    const elapsedMin = Math.round((outMs - inMs) / 60000);
    const workedMin = Number(row.worked_minutes) || 0;
    // Break config is taken from the row's snapshot when present,
    // otherwise from the module's active rule. Iter60.p41 backfill
    // guarantees `break_is_paid` is set on every rule + snapshot.
    const snap = row.shift_rule_snapshot || {};
    const brk = Number(
        snap.break_minutes ?? rule?.break_minutes ?? 0,
    );
    const paid = (snap.break_is_paid ?? rule?.break_is_paid ?? true) !== false;
    const brkLabel = brk > 0
        ? ` · Break ${brk}m (${paid ? "paid" : "unpaid"})`
        : "";
    return `Elapsed ${fmtHM(elapsedMin)}${brkLabel} → Worked ${fmtHM(workedMin)}`;
}


/**
 * Info banner rendered on every module's Daily Attendance page.
 *
 * Behaviour:
 *   * `moduleId` present → fetch active shift rules for that module and
 *     summarise them.
 *   * `moduleId` absent (master view) → renders nothing (there's no
 *     single active rule to describe).
 *
 * Copy always mentions whether the break is paid. Style is a soft
 * info-level banner (navy accent, no alert/warning colours).
 */
export function ActiveShiftRuleBanner({ moduleId, onRulesLoaded }) {
    const [rules, setRules] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!moduleId) {
            setRules(null);
            onRulesLoaded && onRulesLoaded(null);
            return;
        }
        let cancelled = false;
        api.get("/shift-rules", { params: { module_id: moduleId, status: "active" } })
            .then(({ data }) => {
                if (cancelled) return;
                const list = Array.isArray(data) ? data : (data?.items || []);
                setRules(list);
                onRulesLoaded && onRulesLoaded(list);
            })
            .catch((e) => {
                if (cancelled) return;
                setErr(e);
                setRules([]);
                onRulesLoaded && onRulesLoaded([]);
            });
        return () => { cancelled = true; };
    }, [moduleId, onRulesLoaded]);

    // Compose the summary line before any early return so the useMemo
    // hook order stays stable across renders (react-hooks/rules-of-hooks).
    const summary = useMemo(
        () => (Array.isArray(rules) && rules.length > 0 ? summarise(rules) : null),
        [rules],
    );

    // Master view or still-loading → don't render (avoids layout jump).
    if (!moduleId) return null;
    if (rules === null) return null;
    if (rules.length === 0) return null;

    return (
        <div
            className="asl-card px-3 py-2 flex items-start gap-2"
            style={{
                background: "var(--asl-navy-50, #EEF1F8)",
                borderColor: "var(--asl-navy-100, #D5DBEC)",
            }}
            data-testid="attendance-shift-rule-banner"
        >
            <Info className="h-3.5 w-3.5 mt-0.5 asl-navy-text shrink-0" />
            <div
                className="text-xs asl-navy-text leading-snug"
                data-testid="attendance-shift-rule-banner-text"
            >
                {summary}
            </div>
        </div>
    );
}


/**
 * Render the banner's inline summary line as a React node.
 *
 * Uniform break config → single sentence "Break 60m — included in
 * working hours (paid)".
 * Divergent break config → enumerate per rule.
 *
 * The visible strings MUST contain either "included in working hours"
 * (paid) or "deducted from working hours" (unpaid) — the tests check
 * for these exact phrases so the user's Break-Is-Paid rule can be
 * externally verified.
 */
function summarise(rules) {
    if (rules.length === 1) {
        return <SingleRule rule={rules[0]} />;
    }
    const brkMins = new Set(rules.map((r) => Number(r.break_minutes ?? 0)));
    const brkPaid = new Set(rules.map((r) => (r.break_is_paid ?? true) !== false));
    if (brkMins.size === 1 && brkPaid.size === 1) {
        const m = [...brkMins][0];
        const paid = [...brkPaid][0];
        return (
            <>
                <strong>{rules.length} active shift rules</strong>
                {" · "}
                <BreakClause minutes={m} paid={paid} />
            </>
        );
    }
    // Divergent breaks — enumerate.
    return (
        <>
            <strong>{rules.length} active shift rules</strong>
            {" · Break varies: "}
            {rules.map((r, i) => (
                <React.Fragment key={r.id}>
                    {i > 0 && " · "}
                    <span data-testid={`shift-rule-banner-item-${r.id}`}>
                        {r.name}: <BreakClause
                            minutes={Number(r.break_minutes ?? 0)}
                            paid={(r.break_is_paid ?? true) !== false}
                            compact
                        />
                    </span>
                </React.Fragment>
            ))}
        </>
    );
}


function SingleRule({ rule }) {
    const brk = Number(rule.break_minutes ?? 0);
    const paid = (rule.break_is_paid ?? true) !== false;
    // Iter60.p45 — Resolve the effective schedule for TODAY. If a
    // per-day override applies (e.g. Thursday's early finish), the
    // banner surfaces it explicitly so operators reviewing today's
    // attendance see the exact hours the rule is enforcing.
    const eff = _resolveTodayOverride(rule);
    if (eff.isOverride) {
        const dayLabel = eff.dayLabel;
        return (
            <>
                Active shift: <strong>{rule.name}</strong>
                {" · "}
                <strong data-testid="shift-rule-banner-today-override">
                    TODAY ({dayLabel}) {eff.startTime}–{eff.endTime}
                </strong>
                {" · "}
                <BreakClause minutes={eff.breakMinutes} paid={eff.breakPaid} />
            </>
        );
    }
    return (
        <>
            Active shift: <strong>{rule.name}</strong>
            {" · Hours "}
            <span className="mono">
                {rule.start_time}–{rule.end_time}
            </span>
            {" · "}
            <BreakClause minutes={brk} paid={paid} />
        </>
    );
}


// Iter60.p45 — Resolve today's effective schedule from a shift rule's
// `per_day_overrides` map. Returns `{ isOverride, startTime, endTime,
// breakMinutes, breakPaid, dayLabel }`. When no override applies for
// today's weekday, `isOverride` is false and the fields fall back to
// the rule's base values.
const _WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const _WEEKDAY_LABELS = {
    sun: "Sunday", mon: "Monday", tue: "Tuesday", wed: "Wednesday",
    thu: "Thursday", fri: "Friday", sat: "Saturday",
};

function _resolveTodayOverride(rule) {
    const pdo = rule?.per_day_overrides || {};
    const key = _WEEKDAY_KEYS[new Date().getDay()];
    const ov = pdo[key];
    const isWeekend = (rule?.weekend_days || []).includes(new Date().getDay());
    const base = {
        isOverride: false,
        startTime: rule?.start_time,
        endTime: rule?.end_time,
        breakMinutes: Number(rule?.break_minutes ?? 0),
        breakPaid: (rule?.break_is_paid ?? true) !== false,
        dayLabel: _WEEKDAY_LABELS[key] || "",
    };
    if (!ov || Object.keys(ov).length === 0 || isWeekend) return base;
    return {
        isOverride: true,
        startTime: ov.start_time ?? base.startTime,
        endTime: ov.end_time ?? base.endTime,
        breakMinutes: Number(ov.break_minutes ?? base.breakMinutes),
        breakPaid: (ov.break_is_paid ?? base.breakPaid) !== false,
        dayLabel: base.dayLabel,
    };
}


function BreakClause({ minutes, paid, compact }) {
    if (!minutes || minutes <= 0) {
        return <strong>No scheduled break</strong>;
    }
    // Iter60.p41 — the two phrases below are load-bearing: pytest greps
    // the DOM for exactly these substrings. Do not paraphrase without
    // updating `test_iter60p41_banner_and_tooltip`.
    const phrase = paid
        ? "included in working hours (paid)"
        : "deducted from working hours";
    if (compact) {
        return <strong>{minutes}m {paid ? "paid" : "unpaid"}</strong>;
    }
    return (
        <strong>
            Break {minutes}m — {phrase}
        </strong>
    );
}
